export const ENTITY_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;

export type EntityStatus =
  (typeof ENTITY_STATUS)[keyof typeof ENTITY_STATUS];

export const DELIVERY_CHANNEL = {
  IN_APP: "in_app",
  EMAIL: "email",
  SMS: "sms",
  PUSH: "push",
} as const;

export type DeliveryChannel =
  (typeof DELIVERY_CHANNEL)[keyof typeof DELIVERY_CHANNEL];

export const isActiveEntityStatus = (status?: string | null) =>
  status?.toLowerCase() === ENTITY_STATUS.ACTIVE;

export const getEntityToggleStatus = (status?: string | null) =>
  isActiveEntityStatus(status)
    ? ENTITY_STATUS.INACTIVE
    : ENTITY_STATUS.ACTIVE;

export const normalizeEntityFormStatus = (status: unknown) => {
  if (typeof status !== "string") return status;
  const normalized = status.trim().toLowerCase();
  if (normalized === ENTITY_STATUS.INACTIVE) return ENTITY_STATUS.INACTIVE;
  if (normalized === ENTITY_STATUS.ACTIVE) return ENTITY_STATUS.ACTIVE;
  return status;
};

/** Maps legacy PascalCase channel values to lowercase DB values. */
export const normalizeDeliveryChannel = (
  channel: unknown
): DeliveryChannel => {
  if (typeof channel !== "string" || !channel.trim()) {
    return DELIVERY_CHANNEL.EMAIL;
  }
  const lower = channel.trim().toLowerCase();
  if (lower === "in_app" || lower === "inapp") return DELIVERY_CHANNEL.IN_APP;
  if (lower === "email") return DELIVERY_CHANNEL.EMAIL;
  if (lower === "sms") return DELIVERY_CHANNEL.SMS;
  if (lower === "push") return DELIVERY_CHANNEL.PUSH;
  return DELIVERY_CHANNEL.EMAIL;
};

export const CHANNEL_META: Record<
  DeliveryChannel,
  { label: string; badgeColor: string }
> = {
  in_app: { label: "In-App", badgeColor: "light-primary" },
  email: { label: "Email", badgeColor: "light-success" },
  sms: { label: "SMS", badgeColor: "light-warning" },
  push: { label: "Push", badgeColor: "light-info" },
};

export const getChannelMeta = (channel?: string | null) => {
  const key = channel?.toLowerCase() as DeliveryChannel | undefined;
  return (
    (key && CHANNEL_META[key]) ?? {
      label: channel ?? "—",
      badgeColor: "light-secondary",
    }
  );
};

export const PLATFORM_CONFIGURATION_PATH = "/apps/platform-configuration";

export const NOTIFICATION_TEMPLATE_LIST_PATH =
  `${PLATFORM_CONFIGURATION_PATH}/notification-templates`;

export const APP_NOTIFICATION_LIST_PATH =
  `${PLATFORM_CONFIGURATION_PATH}/app-notifications`;

export const NOTIFICATION_LOGS_LIST_PATH =
  `${PLATFORM_CONFIGURATION_PATH}/notification-logs`;
